import { ValidationMode } from '@wix/ambassador-forms-v1-validation-result/types';
import {
  type Form,
  type FormField,
  ValidationFormat,
} from '@wix/ambassador-forms-v4-form/types';
import { type SomeJSONSchema } from 'ajv/dist/types/json-schema';

import { asOptional, asRequired } from '../../utils/lang';

import { type PropertySchemaMapper } from './property-schema-mapper';
import { PropertyTypes } from './property-types';

export type FormSchemaMapper = ReturnType<typeof formSchemaMapperFactory>;

export function formSchemaMapperFactory(
  propertySchemaMapper: PropertySchemaMapper,
  validationMode: ValidationMode,
  libraryBasedPhoneFieldValidationEnabled: boolean = false,
) {
  return function mapToFormSchema(formSchema: Form): SomeJSONSchema {
    const formFields: FormField[] = formSchema.fields || [];
    const formDeletedFields: FormField[] = formSchema.deletedFields || [];
    const fieldsWithValidation = formFields.filter(haveValidation);
    const deletedFieldsWithValidation =
      formDeletedFields.filter(haveValidation);
    const properties = makeValidationProperties(
      fieldsWithValidation,
      deletedFieldsWithValidation,
    );

    const requiredFieldIds =
      validationMode === ValidationMode.UNRESTRICTED_UPDATE
        ? []
        : fieldsWithValidation
            .filter((field) => !isHidden(field))
            .filter((field) => !isReadOnly(field))
            .filter(isRequiredField)
            .map((field) => asRequired(field.target, field, 'target'));

    return {
      $id: asRequired(formSchema.id, formSchema, 'id'),
      type: PropertyTypes.OBJECT,
      properties,
      additionalProperties: false,
      required: requiredFieldIds,
    };
  };

  function makeValidationProperties(
    formFields: FormField[],
    formDeletedFields: FormField[],
  ): Partial<SomeJSONSchema> {
    return [...formFields, ...formDeletedFields].reduce<
      Partial<SomeJSONSchema>
    >((acc, field) => {
      const property = convertFormFieldsToAjvProperties(field);
      if (property) {
        acc[asRequired(field.target, field, 'target')] = property;
      }
      return acc;
    }, {});
  }

  function isHidden(formField: FormField) {
    return formField.hidden;
  }

  function isReadOnly(formField: FormField) {
    return formField.readOnly;
  }

  function isRequiredField(formField: FormField) {
    return (formField?.validation || { required: false }).required;
  }

  function haveValidation(formField: FormField) {
    return (
      Boolean(Object.keys(formField.validation ?? {}).length) ||
      isRequiredField(formField)
    );
  }

  function convertFormFieldsToAjvProperties(
    formField: FormField,
  ): (Omit<SomeJSONSchema, '$id'> & Pick<SomeJSONSchema, '$id'>) | undefined {
    return {
      $id: asRequired(formField.id, formField, 'id'),
      ...(isTargetReferencedField(formField)
        ? { $ref: formField.target }
        : propertySchemaMapper(
            formField.validation,
            asOptional(formField.target),
            validationMode,
            libraryBasedPhoneFieldValidationEnabled,
          )),
    };
  }

  function isTargetReferencedField(formField: FormField) {
    return (
      Boolean(formField.nestedFormId) ||
      formField.validation?.predefined?.format ===
        ValidationFormat.MULTILINE_ADDRESS
    );
  }
}
